/**
 * Оформление категорий отчёта — цвет и подпись для конкретного значения разбивки
 * («Мужской» вместо `1`, свой цвет сектора).
 *
 * Хранится в `visual.category_styles`, потому что это свойство значения данных,
 * а не запроса: `source_request.requests[i].color` описывает серию (метрику/событие),
 * и при разбивке серий становится больше, чем записей в requests — цвет сектора
 * или линии по значению туда просто некуда положить.
 *
 *   visual.category_styles = { "1": { color: "#43A047", label: "Мужской" } }
 *
 * Работает одинаково для SQL-отчётов и обычных отчётов с разбивкой: и там, и там
 * элемент chartData несёт `separators` — список значений разбивки.
 */

// Многоуровневая разбивка склеивается управляющим символом: он не встречается
// в данных, поэтому ключ никогда не столкнётся со значением, содержащим разделитель.
const KEY_SEPARATOR = '\u0001'

/**
 * Ключ значения разбивки в `visual.category_styles`.
 * Для одного уровня (обычный случай) ключ равен самому значению.
 */
export function categoryKey(separators) {
  if (!Array.isArray(separators) || !separators.length) return null
  return separators.map(value => String(value)).join(KEY_SEPARATOR)
}

/**
 * Оформление значения разбивки: `{ color, label }` либо пустой объект.
 */
export function getCategoryStyle(visual, separators) {
  const key = categoryKey(separators)
  if (!key) return {}
  return visual?.category_styles?.[key] || {}
}

/**
 * Подпись значения разбивки: пользовательская, иначе исходные значения через « - ».
 */
export function categoryLabel(visual, separators) {
  if (!Array.isArray(separators) || !separators.length) return ''
  const custom = getCategoryStyle(visual, separators).label
  if (custom) return custom
  return separators.map(value => String(value)).join(' - ')
}

/**
 * Патч `visual` с изменённым оформлением значения — возвращает новый объект,
 * чтобы вызывающий код (мутация стора / изолированное состояние) просто присвоил его.
 */
export function withCategoryStyle(visual, separators, patch) {
  const key = categoryKey(separators)
  const base = visual || {}
  if (!key) return base
  const styles = { ...(base.category_styles || {}) }
  const next = { ...(styles[key] || {}), ...patch }

  // Пустые значения не храним: сброс цвета возвращает палитру, сброс подписи — исходное значение
  for (const field of Object.keys(next)) {
    if (next[field] === null || next[field] === undefined || next[field] === '') delete next[field]
  }

  if (Object.keys(next).length) styles[key] = next
  else delete styles[key]

  return { ...base, category_styles: styles }
}

export default { categoryKey, getCategoryStyle, categoryLabel, withCategoryStyle }
